import { useState, useMemo, useCallback, useEffect } from 'react';
import { applyResultsFilter } from '../utils/resultsFilter';

/**
 * Hook to manage results filtering state and logic
 * @param {Object} searchResults - Original search results
 * @returns {Object} Filter state and functions
 */
export const useResultsFilter = (searchResults) => {
  // Store original unfiltered results
  const [originalSearchResults, setOriginalSearchResults] = useState(null);
  
  // Filter criteria: selected lease keys
  const [selectedLeaseKeys, setSelectedLeaseKeys] = useState(new Set());
  
  // Track if filter is active
  const isFilterActive = selectedLeaseKeys.size > 0;

  // Update original results when search results change (only if not filtering)
  useEffect(() => {
    if (searchResults && !isFilterActive) {
      setOriginalSearchResults(searchResults);
    }
  }, [searchResults, isFilterActive]);

  // Apply filter to create filtered results
  const filteredResults = useMemo(() => {
    if (!isFilterActive || !searchResults) {
      return searchResults;
    }
    return applyResultsFilter(searchResults, selectedLeaseKeys);
  }, [searchResults, selectedLeaseKeys, isFilterActive]);

  // Add lease keys to selection
  const addLeaseKeys = useCallback((leaseKeys) => {
    setSelectedLeaseKeys(prev => {
      const newSet = new Set(prev);
      const keysArray = Array.isArray(leaseKeys) ? leaseKeys : [leaseKeys];
      keysArray.forEach(key => {
        if (key) newSet.add(key);
      });
      return newSet;
    });
  }, []);

  // Remove lease keys from selection
  const removeLeaseKeys = useCallback((leaseKeys) => {
    setSelectedLeaseKeys(prev => {
      const newSet = new Set(prev);
      const keysArray = Array.isArray(leaseKeys) ? leaseKeys : [leaseKeys];
      keysArray.forEach(key => {
        newSet.delete(key);
      });
      return newSet;
    });
  }, []);

  // Clear all selections
  const clearFilter = useCallback(() => {
    setSelectedLeaseKeys(new Set());
  }, []);

  // Set lease keys (replace existing)
  const setLeaseKeys = useCallback((leaseKeys) => {
    const keysSet = leaseKeys instanceof Set ? leaseKeys : new Set(Array.isArray(leaseKeys) ? leaseKeys : [leaseKeys]);
    setSelectedLeaseKeys(keysSet);
  }, []);

  return {
    // State
    selectedLeaseKeys,
    filteredResults: filteredResults || searchResults,
    originalSearchResults: originalSearchResults || searchResults,
    isFilterActive,
    
    // Actions
    addLeaseKeys,
    removeLeaseKeys,
    setLeaseKeys,
    clearFilter,
  };
};

